/**
 * Immich (セルフホスト写真管理) API サービス
 *
 * バックエンドが API キーを保持しプロキシするため、
 * フロント側は認証ヘッダを別途持たない。
 */

import { apiClient, resolveDirectUrl } from '@/api/client'

export type ImmichAssetType = 'IMAGE' | 'VIDEO' | string

export type ImmichAsset = {
  id: string
  type: ImmichAssetType
  /** ISO8601 UTC (末尾 Z 付き) */
  fileCreatedAt: string
  originalFileName: string
  /** ミリ秒 (Immich v3 以降) */
  duration: number | null
}

export type ImmichAssetMetadata = {
  city: string | null
  state: string | null
  country: string | null
  latitude: number | null
  longitude: number | null
  make: string | null
  model: string | null
  lensModel: string | null
}

/** Visit ごとにグルーピングされた写真 (Place 詳細画面向け) */
export type VisitPhotoGroup = {
  visit_id: number
  /** ISO8601 UTC (末尾 Z) */
  arrival_date: string | null
  departure_date: string | null
  photos: ImmichAsset[]
}

/**
 * 指定日 (JST) に撮影された Immich アセット一覧を取得
 * @param date YYYY-MM-DD
 */
export const getImmichAssetMetadata = async (assetId: string): Promise<ImmichAssetMetadata> => {
  const res = await apiClient.get<ImmichAssetMetadata>(`/immich/assets/${assetId}/metadata`)
  return res.data
}

export const getImmichAssetsByDate = async (date: string): Promise<ImmichAsset[]> => {
  const res = await apiClient.get<ImmichAsset[]>('/immich/assets', {
    params: { date },
  })
  return res.data
}

/**
 * サムネイルの URL を組み立てる
 * @param assetId Immich アセット UUID
 * @param size 'thumbnail' | 'preview'
 */
export const buildImmichThumbnailUrl = (assetId: string, size: 'thumbnail' | 'preview' = 'preview'): string => {
  return resolveDirectUrl(`/api/immich/thumbnail/${assetId}?size=${size}`)
}

/**
 * 指定 Place に紐付く全 Visit の写真を Visit 単位でグルーピングして取得
 * (Immich API は叩かず DB キャッシュのみを参照する軽量エンドポイント)
 */
export const getImmichAssetsByPlace = async (placeId: number): Promise<{ groups: VisitPhotoGroup[] }> => {
  const res = await apiClient.get<{ groups: VisitPhotoGroup[] }>(`/immich/assets/by-place/${placeId}`)
  return res.data
}

/**
 * Immich delta sync を手動発火 (未取得の新規/更新 asset を DB に取り込む)
 * @param force スロットルを無視して強制実行
 */
export const triggerImmichDeltaSync = async (force = false): Promise<Record<string, unknown>> => {
  const res = await apiClient.post<Record<string, unknown>>('/immich/sync/delta', undefined, {
    params: { force },
  })
  return res.data
}

/**
 * 動画再生用の短命署名付き相対 URL を取得
 * バックエンドが `/immich/asset/{id}/video?exp=&uid=&sig=` を返す。
 * <video>/ReactPlayer には resolveDirectUrl で絶対 URL 化してから渡す。
 */
export const getImmichVideoPlaybackUrl = async (assetId: string): Promise<string> => {
  const res = await apiClient.get<{ url: string; ttl_sec: number }>(`/immich/asset/${assetId}/playback-url`)
  // "/immich/..." → "/api/immich/..." に整えてから絶対化
  const relative = res.data.url.startsWith('/api/') ? res.data.url : `/api${res.data.url}`
  return resolveDirectUrl(relative)
}
